import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Warning, X } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { btnClass } from '../common/buttonClasses';
import http from '../services/http';
import {
    getImpersonationOrigin,
    IMPERSONATION_FORBIDDEN_EVENT,
    restoreImpersonationOrigin,
} from '../utils/impersonation';

/**
 * While an advisor is assumed into a client entity, their own pages (entities, commissions)
 * keep reloading from browser history and get refused, because the session now belongs to the
 * client user. Those refusals are silent, so the page just looks broken. Prompt the advisor
 * to switch back instead.
 */
const ImpersonationAccessNotice: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [origin, setOrigin] = useState<any | null>(null);

    useEffect(() => {
        const showNotice = () => setOrigin(getImpersonationOrigin());
        window.addEventListener(IMPERSONATION_FORBIDDEN_EVENT, showNotice);
        return () => window.removeEventListener(IMPERSONATION_FORBIDDEN_EVENT, showNotice);
    }, []);

    const handleSwitchBack = useCallback(() => {
        setOrigin(null);
        const restored = restoreImpersonationOrigin();
        if (!restored) return;
        http.setAuthToken(restored.token);
        navigate('/entities');
        navigate(0);
    }, [navigate]);

    if (!origin) return null;

    const originName = origin.user?.firstName;

    return (
        <div className="toast toast-warning" role="status">
            <Warning size={20} />
            <span>
                {t(
                    'header.assumedAccessNotice',
                    'This page needs your own account. Switch back to view it.'
                )}
            </span>
            <button
                className={btnClass({ variant: 'primary', className: 'switch-back-button' })}
                onClick={handleSwitchBack}
            >
                {originName
                    ? t('header.backToAccount', "Back to {{name}}'s account", { name: originName })
                    : t('header.backToOwnAccount', 'Back to my account')}
            </button>
            <button
                onClick={() => setOrigin(null)}
                className="toast-close"
                aria-label={t('actions.close', 'Close')}
            >
                <X size={18} />
            </button>
        </div>
    );
};

export default ImpersonationAccessNotice;
